import { useEffect, useRef } from 'react'

interface UseCanvasTabShortcutOptions {
  /** Nur im Maus-Werkzeug des sichtbaren Tabs (TabStack-Falle). */
  enabled: boolean
  /**
   * Das Canvas-Element, an dem gehorcht wird — NICHT das `window`.
   * `null`, solange React Flow seine Pane noch nicht aufgebaut hat.
   */
  container: HTMLElement | null
  /**
   * Einen Schritt in der **Tab-Reihenfolge**. Meldet zurück, ob es etwas
   * getan hat — war nichts ausgewählt, gehört die Taste wieder dem Browser.
   */
  onStep: (direction: 1 | -1) => boolean
  /** Aus dem **Bearbeiten-Modus** heraus einen neuen Notizen daneben legen. */
  onSpawn: (fromNoteId: string) => void
}

/**
 * Tab bewegt die Auswahl durch die **Canvas-Elemente**; im **Bearbeiten-Modus**
 * legt es stattdessen einen neuen **Notizen** daneben (wie in Miro). ⇧Tab
 * geht die Reihenfolge zurück.
 *
 * **Am Canvas-Element und nicht am `window`** — dieselbe Begründung wie bei
 * `useCanvasDuplicateShortcut` und `useCanvasEscape`: Auf dem Weg nach oben
 * läuft der Tastendruck zuerst hier durch, `stopPropagation()` hält ihn also
 * auf, bevor ihn jemand anderes sieht, unabhängig von der Reihenfolge der
 * Registrierungen.
 *
 * **Abgefangen wird nur, was wir auch verarbeiten.** Tab ist DIE Taste der
 * Tastaturbedienung; sie bedingungslos zu nehmen, sobald ein Canvas offen ist,
 * nähme dem Rest der App die Tastaturnavigation. Ist nichts ausgewählt, meldet
 * `onStep` das mit `false`, und das Ereignis läuft unangetastet weiter.
 *
 * Der Canvas hat dadurch keine zweite, konkurrierende Reihenfolge: React Flow
 * macht seine Nodes ohnehin fokussierbar (`nodesFocusable`), Tab lief also
 * schon vorher durch die Notizen — nur in Anlegereihenfolge und ohne dabei
 * etwas auszuwählen. Wir ersetzen diese Reihenfolge, wir erfinden sie nicht.
 *
 * @example
 * useCanvasTabShortcut({ enabled, container: canvasElement, onStep, onSpawn })
 */
export function useCanvasTabShortcut({
  enabled,
  container,
  onStep,
  onSpawn,
}: UseCanvasTabShortcutOptions) {
  // Über Refs, damit der Listener nicht bei jeder Änderung an der Auswahl neu
  // gebunden wird — während eines Ziehvorgangs wäre das jeder Frame.
  const onStepRef = useRef(onStep)
  onStepRef.current = onStep
  const onSpawnRef = useRef(onSpawn)
  onSpawnRef.current = onSpawn

  useEffect(() => {
    if (!enabled || !container) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      // ⌘⇥ und ⌥⇥ gehören dem Betriebssystem, ⌃⇥ dem Tab-Wechsel.
      if (e.metaKey || e.ctrlKey || e.altKey) return
      // Ein gehaltenes Tab soll weder durch die Notizen rasen noch einen
      // Stapel neuer anlegen. Dieselbe Sperre führt ⌘D.
      if (e.repeat) return
      // Einem offenen Menü seine Tasten lassen.
      if (document.querySelector('[role="menu"]')) return

      const target = e.target as HTMLElement | null

      // ── Im Bearbeiten-Modus: einen neuen Notizen daneben ──
      //
      // Geprüft wird `readOnly`, NICHT bloß der Tag-Name. Der `NoteNode`
      // rendert sein Textfeld immer und setzt nur `readOnly`, wenn nicht
      // bearbeitet wird; sein eigener Kommentar hält fest, dass ein
      // schreibgeschütztes Feld weiterhin Fokus annimmt. Über `TEXTAREA`
      // allein entstünde also eine Notiz, obwohl niemand im Text war —
      // erreichbar, sobald der native Tab einmal in einem ruhenden Feld
      // geparkt hat.
      if (target instanceof HTMLTextAreaElement && !target.readOnly) {
        // ⇧Tab legt nichts an: „rückwärts anlegen" hat keine klare Bedeutung —
        // links? vor dem aktuellen in der Reihenfolge? Beides wäre geraten.
        if (e.shiftKey) return
        const noteId = target.closest<HTMLElement>('.react-flow__node')?.dataset.id
        if (!noteId) return

        e.preventDefault()
        e.stopPropagation()
        onSpawnRef.current(noteId)
        return
      }

      // ── Sonst: einen Schritt in der Reihenfolge ──
      if (!onStepRef.current(e.shiftKey ? -1 : 1)) return

      e.preventDefault()
      e.stopPropagation()
    }

    container.addEventListener('keydown', handleKeyDown)
    return () => container.removeEventListener('keydown', handleKeyDown)
  }, [enabled, container])
}
